import api from "../api.service"
import chunk from "lodash/chunk"
import pLimit from "p-limit"
import { createAbortManager, delay } from "../../utils/utils"

export function getNextFn(nextLink) {
    return ({ signal } = {}) => {
        return api
            .get(`/v2/${nextLink.split("/v2/")[1]}`, { signal })
            .then((res) => getPaginatedResponse(res.data))
            .catch((err) => {
                if (err.name === "CanceledError") {
                    return {
                        value: [],
                        nextLink: null,
                        hasNext: () => false,
                    }
                } else {
                    throw api.getResponseError(err)
                }
            })
    }
}

export function getPaginatedResponse(response) {
    return {
        ...response,
        getNext: response.nextLink ? getNextFn(response.nextLink) : () => Promise.resolve([]),
        hasNext: () => !!response.nextLink,
    }
}

export async function paginateAll(requestPromise, { signal, delayMs, limit = Infinity, maxPages = Infinity } = {}) {
    signal?.throwIfAborted()

    let response
    try {
        response = await requestPromise
    } catch (err) {
        throw typeof api !== "undefined" ? api.getResponseError(err) : err
    }

    let list = Array.isArray(response?.value) ? [...response.value] : []
    let pageCount = 1

    if (list.length >= limit) {
        return list.slice(0, limit)
    }

    while (response && typeof response.hasNext === "function" && response.hasNext() && list.length < limit) {
        signal?.throwIfAborted()

        if (pageCount >= maxPages) {
            break
        }

        if (delayMs > 0) {
            await delay(delayMs)
            signal?.throwIfAborted() // Check abort signal after delay
        }

        let nextResponse
        try {
            nextResponse = await response.getNext() // no signal assumption
        } catch (err) {
            throw typeof api !== "undefined" ? api.getResponseError(err) : err
        }

        const pageData = Array.isArray(nextResponse?.value) ? nextResponse.value : []

        if (pageData.length === 0 && nextResponse?.hasNext?.()) {
            break
        }

        const remaining = limit - list.length

        if (pageData.length > remaining) {
            list.push(...pageData.slice(0, remaining))
            break
        }

        list.push(...pageData)

        response = nextResponse
        pageCount++
    }

    return list || []
}

export async function paginateChunked({
    fn,
    params,
    chunkKey,
    chunkSize = 50,
    merge = (acc, res) => acc.concat(res),
    concurrency = 1,
    signal,
}) {
    const values = params[chunkKey]

    if (values == null) {
        // abort before even calling fn
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError")
        return fn(params)
    }

    if (!Array.isArray(values)) {
        throw new Error(`"${chunkKey}" must be an array when provided`)
    }

    const chunks = chunk(values, chunkSize)
    let results = []

    // Create an AbortManager to manage the signal cleanly
    const abortManager = createAbortManager(signal)

    // Sequential execution
    if (concurrency === 1) {
        for (const part of chunks) {
            if (abortManager.isAborted()) {
                throw new DOMException("Aborted", "AbortError")
            }

            try {
                const res = await abortable(
                    fn({
                        ...params,
                        [chunkKey]: part,
                    }),
                    signal,
                )
                results = merge(results, res)
            } catch (err) {
                if (err.name === "AbortError") throw err
                console.warn("Chunk failed, skipping:", err)
            }
        }
        return results
    }

    // Concurrent execution
    const limit = pLimit(concurrency)

    const promises = chunks.map((part) =>
        limit(async () => {
            if (abortManager.isAborted()) {
                throw new DOMException("Aborted", "AbortError")
            }

            try {
                return await abortable(
                    fn({
                        ...params,
                        [chunkKey]: part,
                    }),
                    signal,
                )
            } catch (err) {
                if (err.name === "AbortError") throw err
                console.warn("Chunk failed, skipping:", err)
                return null
            }
        }),
    )

    const chunkResults = await Promise.all(promises)

    for (const res of chunkResults) {
        if (res != null) {
            results = merge(results, res)
        }
    }

    return results
}

export async function fetchByChunks({ fn, params, chunkKey, chunkSize = 50, concurrency = 3, signal }) {
    const ids = params[chunkKey] || []
    const chunks = chunk(ids, chunkSize)
    const limit = pLimit(concurrency)

    const promises = chunks.map((batch) =>
        limit(async () => {
            if (signal?.aborted) throw new DOMException("Aborted", "AbortError")

            try {
                // Return the WHOLE response object for this chunk
                return await abortable(fn({ ...params, [chunkKey]: batch }), signal)
            } catch (err) {
                if (err.name === "AbortError") throw err
                console.warn("Chunk failed:", err)
                return null // Return null so you know this specific chunk dropped
            }
        }),
    )

    // This returns [Resp1, Resp2, Resp3...]
    return Promise.all(promises)
}

export async function paginateChunkedX({
    fn,
    params,
    chunkKey,
    chunkSize = 50,
    merge = (acc, res) => acc.concat(res),
    concurrency = 1,
    signal,
}) {
    const values = params[chunkKey]

    if (values == null) {
        return fn(params)
    }

    if (!Array.isArray(values)) {
        throw new Error(`"${chunkKey}" must be an array when provided`)
    }

    const chunks = chunk(values, chunkSize)
    let results = []

    // Sequential execution
    if (concurrency === 1) {
        for (const part of chunks) {
            try {
                const res = await fn({
                    ...params,
                    [chunkKey]: part,
                })
                results = merge(results, res)
            } catch (err) {
                console.warn("Chunk failed, skipping:", err)
            }
        }
        return results
    }

    // Concurrent execution
    const limit = pLimit(concurrency)

    const promises = chunks.map((part) =>
        limit(async () => {
            try {
                return await fn({
                    ...params,
                    [chunkKey]: part,
                })
            } catch (err) {
                console.warn("Chunk failed, skipping:", err)
                return null
            }
        }),
    )

    const chunkResults = await Promise.all(promises)

    for (const res of chunkResults) {
        if (res != null) {
            results = merge(results, res)
        }
    }

    return results
}

function abortable(promise, signal) {
    if (!signal) return promise

    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException("Aborted", "AbortError"))
            return
        }

        const onAbort = () => {
            reject(new DOMException("Aborted", "AbortError"))
        }

        signal.addEventListener("abort", onAbort, { once: true })

        promise
            .then((res) => {
                // remove listener immediately when done
                signal.removeEventListener("abort", onAbort)
                resolve(res)
            })
            .catch((err) => {
                signal.removeEventListener("abort", onAbort)
                reject(err)
            })
    })
}
